import React, { useState, useEffect } from 'react';
import { useProducts } from '../context/ProductContext';
import AdminAuth from '../components/AdminAuth';
import '../styles/Admin.css';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('chocolate');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Move useProducts to the top level - FIXED!
  const { products, addProduct, updateProduct, deleteProduct, resetProducts, loading } = useProducts();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    imageFile: null,
    sizes: [{ size: '', price: '' }],
    featured: false
  });

  useEffect(() => {
    const adminAuth = sessionStorage.getItem('cakified_admin');
    if (adminAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('cakified_admin');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

 const handleImageUpload = (e) => {
  const file = e.target.files[0];
  if (file) {
    // Check file size (max 2MB before compression)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size should be less than 2MB. Please choose a smaller image.');
      e.target.value = ''; // Clear the input
      return;
    }

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setFormData(prev => ({
        ...prev,
        image: reader.result,
        imageFile: file
      }));
    };
    reader.readAsDataURL(file);
  }
};

  const handleSizeChange = (index, field, value) => {
    const newSizes = [...formData.sizes];
    newSizes[index] = { ...newSizes[index], [field]: value };
    setFormData(prev => ({ ...prev, sizes: newSizes }));
  };

  const addSizeRow = () => {
    setFormData(prev => ({
      ...prev,
      sizes: [...prev.sizes, { size: '', price: '' }]
    }));
  };

  const removeSizeRow = (index) => {
    if (formData.sizes.length > 1) {
      const newSizes = formData.sizes.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, sizes: newSizes }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validSizes = formData.sizes.filter(s => s.size.trim() && s.price);
    
    if (!formData.name || !formData.description || !formData.image || validSizes.length === 0) {
      alert('Please fill in all required fields including at least one size with price');
      return;
    }

    try {
      const productData = {
        name: formData.name,
        description: formData.description,
        image: formData.image,
        imageFile: formData.imageFile,
        sizes: validSizes.map(s => ({ 
          size: s.size.trim(), 
          price: Number(s.price) 
        })),
        featured: formData.featured
      };

      const isCakeSubCategory = ['chocolate', 'pineapple', 'customized'].includes(activeTab);
      
      if (editingProduct) {
        if (isCakeSubCategory) {
          await updateProduct('cakes', editingProduct.id, productData, activeTab);
        } else {
          await updateProduct(activeTab, editingProduct.id, productData);
        }
        alert('Product updated successfully!');
      } else {
        if (isCakeSubCategory) {
          await addProduct('cakes', productData, activeTab);
          alert(`Product added to ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Cakes!`);
        } else {
          await addProduct(activeTab, productData);
          alert('Product added successfully!');
        }
      }

      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product. Please try again.');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      image: product.image,
      imageFile: null,
      sizes: product.sizes.map(s => ({ ...s })),
      featured: product.featured || false
    });
    setImagePreview(product.image);
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const isCakeSubCategory = ['chocolate', 'pineapple', 'customized'].includes(activeTab);
        
        if (isCakeSubCategory) {
          await deleteProduct('cakes', productId, activeTab);
        } else {
          await deleteProduct(activeTab, productId);
        }
        
        alert('Product deleted successfully!');
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error deleting product. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image: '',
      imageFile: null,
      sizes: [{ size: '', price: '' }],
      featured: false
    });
    setEditingProduct(null);
    setShowForm(false);
    setImagePreview(null);
  };

  // const handleResetData = () => {
  //   if (window.confirm('Are you sure you want to reset all products to initial data? This cannot be undone.')) {
  //     resetProducts();
  //     alert('Products reset successfully!');
  //   }
  // };

  // Loading state
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div className="spinner" style={{ width: '50px', height: '50px' }}></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminAuth onAuthenticate={setIsAuthenticated} />;
  }

  // Get current products based on active tab
  const currentProducts = 
    activeTab === 'chocolate' ? products.cakes?.chocolate || [] :
    activeTab === 'pineapple' ? products.cakes?.pineapple || [] :
    activeTab === 'customized' ? products.cakes?.customized || [] :
    activeTab === 'jarCakes' ? products.jarCakes || [] :
    products.brownies || [];

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-title">
            <img src="/logo.png" alt="Cakified Logo" />
            <h1>Admin Dashboard</h1>
          </div>
          <div className="admin-actions">
            {/* <button onClick={handleResetData} className="btn btn-outline" title="Reset to initial products">
              Reset Data
            </button> */}
            <button onClick={handleLogout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="admin-container">
        <div className="admin-tabs">
          <button
            className={`tab-btn ${activeTab === 'chocolate' ? 'active' : ''}`}
            onClick={() => { setActiveTab('chocolate'); resetForm(); }}
          >
            Chocolate Cakes ({products.cakes?.chocolate?.length || 0})
          </button>
          <button
            className={`tab-btn ${activeTab === 'pineapple' ? 'active' : ''}`}
            onClick={() => { setActiveTab('pineapple'); resetForm(); }}
          >
            Pineapple Cakes ({products.cakes?.pineapple?.length || 0})
          </button>
          <button
            className={`tab-btn ${activeTab === 'customized' ? 'active' : ''}`}
            onClick={() => { setActiveTab('customized'); resetForm(); }}
          >
            Customized Cakes ({products.cakes?.customized?.length || 0})
          </button>
          <button
            className={`tab-btn ${activeTab === 'jarCakes' ? 'active' : ''}`}
            onClick={() => { setActiveTab('jarCakes'); resetForm(); }}
          >
            Jar Cakes ({products.jarCakes?.length || 0})
          </button>
          <button
            className={`tab-btn ${activeTab === 'brownies' ? 'active' : ''}`}
            onClick={() => { setActiveTab('brownies'); resetForm(); }}
          >
            Brownies ({products.brownies?.length || 0})
          </button>
        </div>

        <div className="admin-content">
          <div className="admin-toolbar">
            <h2>
              {activeTab === 'chocolate' ? 'Chocolate Cakes' : 
               activeTab === 'pineapple' ? 'Pineapple Cakes' :
               activeTab === 'customized' ? 'Customized Cakes' :
               activeTab === 'jarCakes' ? 'Jar Cakes' : 'Brownies'}
            </h2>
            <button
              onClick={() => setShowForm(!showForm)}
              className="btn btn-primary"
            >
              {showForm ? 'Cancel' : '+ Add New Product'}
            </button>
          </div>

          {showForm && (
            <div className="product-form-container">
              <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
              <form onSubmit={handleSubmit} className="product-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Product Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Chocolate Dream Cake"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="imageUpload">Product Image *</label>
                    <input
                      type="file"
                      id="imageUpload"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="file-input"
                    />
                    <small>Max size: 5MB. Formats: JPG, PNG, WebP</small>
                  </div>
                </div>

                {imagePreview && (
                  <div className="image-preview-container">
                    <img src={imagePreview} alt="Preview" className="image-preview" />
                    <button 
                      type="button" 
                      onClick={() => {
                        setImagePreview(null);
                        setFormData(prev => ({ ...prev, image: '', imageFile: null }));
                      }}
                      className="remove-image-btn"
                    >
                      ✕ Remove
                    </button>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="description">Description *</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe your product..."
                    rows="3"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Sizes & Prices *</label>
                  {formData.sizes.map((sizeObj, index) => (
                    <div key={index} className="size-price-row">
                      <input
                        type="text"
                        placeholder="Size (e.g., 0.5kg)"
                        value={sizeObj.size}
                        onChange={(e) => handleSizeChange(index, 'size', e.target.value)}
                        required
                      />
                      <input
                        type="number"
                        placeholder="Price (₹)"
                        value={sizeObj.price}
                        onChange={(e) => handleSizeChange(index, 'price', e.target.value)}
                        min="0"
                        required
                      />
                      {formData.sizes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSizeRow(index)}
                          className="btn-remove-size"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addSizeRow}
                    className="btn-add-size"
                  >
                    + Add Another Size
                  </button>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                    />
                    <span>Mark as Featured/Popular</span>
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingProduct ? 'Update Product' : 'Add Product'}
                  </button>
                  <button type="button" onClick={resetForm} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="products-list">
            {currentProducts.length === 0 ? (
              <div className="empty-state">
                <p>No products added yet. Click "Add New Product" to get started!</p>
              </div>
            ) : (
              <div className="products-grid">
                {currentProducts.map(product => (
                  <div key={product.id} className="admin-product-card">
                    <div className="product-preview">
                      <img src={product.image} alt={product.name} />
                      {product.featured && <span className="featured-badge">Popular</span>}
                    </div>
                    <div className="product-info">
                      <h4>{product.name}</h4>
                      <p>{product.description}</p>
                      <div className="product-meta">
                        {product.sizes.map((s, i) => (
                          <span key={i} className="size-price-tag">
                            {s.size}: ₹{s.price}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="product-actions-btns">
                      <button
                        onClick={() => handleEdit(product)}
                        className="btn-edit"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;